import * as v from 'valibot'
import dayjs from 'dayjs'

import { getTODSchema } from '../shared-schemas/tods'
import { IOptimusTod as IOptimusTODv1 } from '@shared-types/optimus'
import { getOptimusTimeOfDaySchema } from '../shared-schemas/optimus/optimus-config-v2'
import { ITodQueryParam } from '@shared-schemas/metrics-io'

const TODv1Schema = getTODSchema({expected_uuid_format: 'full'})
type ITODv1 = v.InferInput<typeof TODv1Schema>

const OptimusTODv2Schema = getOptimusTimeOfDaySchema({expected_format: 'full'})
type IOptimusTODv2 = v.InferInput<typeof OptimusTODv2Schema>

export function containsStartEndTimes(str: string) {
	return /.*(\d:\d\d).*(\d:\d\d).*/g.test(str)
} 

// Long time range label: "10:00AM - 07:00PM"
export function formatTimeRangeLong(local_start_time: string, local_end_time: string) {
	const start = local_start_time ? dayjs(local_start_time, 'HH:mm').format('hh:mmA') : ''
	const end = local_end_time ? dayjs(local_end_time, 'HH:mm').format('hh:mmA') : ''
	return `${start} - ${end}`
}

type IFormatTextParamsTODv1 = Pick<
	ITODv1,
	'id' |
	'name' |
	'local_start_time' |
	'local_end_time' |
	'has_monday' |
	'has_tuesday' |
	'has_wednesday' |
	'has_thursday' |
	'has_friday' |
	'has_saturday' |
	'has_sunday'
> & {
	// Backwards compat?
	tod_name?: string
}
type IFormatTextParamsOptimusTODv1 = Pick<
	IOptimusTODv1,
	'time_of_day_id' |
	'name' |
	'local_start_time' |
	'local_end_time' |
	'time_of_day_data'
> & {
	// Backwards compat?
	days: (
		'Monday' |
		'Tuesday' |
		'Wednesday' |
		'Thursday' |
		'Friday' |
		'Saturday' |
		'Sunday'
	)[],
}
type IFormatTextParamsOptimusTODv2 = IOptimusTODv2
type FormatTODTextParams = IFormatTextParamsTODv1 | IFormatTextParamsOptimusTODv1 | IFormatTextParamsOptimusTODv2 | ITodQueryParam

function isOptimusTODv2(tod: FormatTODTextParams): tod is IFormatTextParamsOptimusTODv2 {
	return 'tod_group_id' in tod && tod.tod_group_id != null
}
function isOptimusTODv1(tod: FormatTODTextParams): tod is IFormatTextParamsOptimusTODv1 {
	return !isOptimusTODv2(tod) && 'time_of_day_id' in tod && tod.time_of_day_id != null
}
function isTODv1WithName(tod: FormatTODTextParams): tod is IFormatTextParamsTODv1 {
	return !isOptimusTODv1(tod) && ('name' in tod || 'tod_name' in tod)
}

export const formatTODText = (tod: FormatTODTextParams) => {

	let name = ''

	// OPTIMUS TOD v2 NOT YET SUPPORTED
	if (isOptimusTODv2(tod)) {

		// name += daysToShortLabel(tod.days)

		// const months_label = monthsToShortLabel(tod.months)
		// const doms_label = domsToShortLabel(tod.days_of_month)
		// const segments = [months_label, doms_label].filter(Boolean)
		// if (segments.length) name += ` (${segments.join(', ')})`
		// name += ' '
		throw Error(`Optimus TOD v2 not yet supported: ${tod}`)
	}
	else if (isOptimusTODv1(tod)) {

		let days = tod.time_of_day_data.days || tod.days

		// ???: Backwards compat for tod.days form?
		// Normalize days[] to boolean object format
		if (Array.isArray(days)) {
			days = {
				monday: days.includes('Monday'),
				tuesday: days.includes('Tuesday'),
				wednesday: days.includes('Wednesday'),
				thursday: days.includes('Thursday'),
				friday: days.includes('Friday'),
				saturday: days.includes('Saturday'),
				sunday: days.includes('Sunday'),
			}
		}

		if (days.monday && days.tuesday && days.wednesday && days.thursday && days.friday && days.saturday && days.sunday) {
			name += 'Daily'
		} else if (
			days.monday &&
			days.tuesday &&
			days.wednesday &&
			days.thursday &&
			days.friday &&
			!days.saturday &&
			!days.sunday
		) {
			name += 'Weekdays'
		} else if (
			!days.monday &&
			!days.tuesday &&
			!days.wednesday &&
			!days.thursday &&
			!days.friday &&
			days.saturday &&
			days.sunday
		) {
			name += 'Weekends'
		} else {
			const d = []
			if (days.monday) d.push('Mo')
			if (days.tuesday) d.push('Tu')
			if (days.wednesday) d.push('We')
			if (days.thursday) d.push('Th')
			if (days.friday) d.push('Fr')
			if (days.saturday) d.push('Sa')
			if (days.sunday) d.push('Su')
			name += d.join(', ')
		}
	}
	else {

		if (isTODv1WithName(tod)) {

			name = tod.name || tod.tod_name || ''

			if (
				tod.name === 'All Periods'
				|| containsStartEndTimes(tod.name)
			) {
				return name
			}

			return name += `: ${formatTimeRangeLong(tod.local_start_time, tod.local_end_time)}`

		}

		if (
			tod.has_monday &&
			tod.has_tuesday &&
			tod.has_wednesday &&
			tod.has_thursday &&
			tod.has_friday &&
			tod.has_saturday &&
			tod.has_sunday &&
			tod?.local_start_time?.startsWith('00:00') &&
			tod?.local_end_time?.startsWith('23:59')
		) {
			return 'All Periods'
		}

		if (
			tod.has_monday &&
			tod.has_tuesday &&
			tod.has_wednesday &&
			tod.has_thursday &&
			tod.has_friday &&
			tod.has_saturday &&
			tod.has_sunday
		) {
			name += 'Daily'
		} else if (
			tod.has_monday &&
			tod.has_tuesday &&
			tod.has_wednesday &&
			tod.has_thursday &&
			tod.has_friday &&
			!tod.has_saturday &&
			!tod.has_sunday
		) {
			name += 'Weekdays'
		} else if (
			!tod.has_monday &&
			!tod.has_tuesday &&
			!tod.has_wednesday &&
			!tod.has_thursday &&
			!tod.has_friday &&
			tod.has_saturday &&
			tod.has_sunday
		) {
			name += 'Weekends'
		} else {
			const d = []
			if (tod.has_monday) d.push('Mo')
			if (tod.has_tuesday) d.push('Tu')
			if (tod.has_wednesday) d.push('We')
			if (tod.has_thursday) d.push('Th')
			if (tod.has_friday) d.push('Fr')
			if (tod.has_saturday) d.push('Sa')
			if (tod.has_sunday) d.push('Su')
			name += d.join(', ')
		}
	}
	
	name += `: ${formatTimeRangeLong(tod.local_start_time, tod.local_end_time)}`

	return name
}
